import {Action, Actions, FilterState, SortOrders, State, TimeWindow} from './types';
import {FilterOperator} from '@cervello/use-filters';
import {queryStringify} from '@cervello/utils';

export const initialState = {
  isLoading: false,
  pagination: {
    limit: 10,
    page: 1,
    count: 0,
  },
  // filters: null,
  sort: {},
  // search: undefined,
};

export const getTimeFilter = (
  time: TimeWindow,
):
  | {
      key: 'time';
      operator: FilterOperator;
      value: number | number[];
    }
  | undefined => {
  let operator: FilterOperator = 'between';

  if (time.rangeOptions) {
    if (time.rangeOptions.start && time.rangeOptions.end) {
      operator = 'between';
    } else if (time.rangeOptions.start) {
      operator = 'gte';
    } else if (time.rangeOptions.end) {
      operator = 'lte';
    }

    return {
      key: 'time',
      operator,
      value:
        time.rangeOptions.start && time.rangeOptions.end
          ? [time.rangeOptions.start, time.rangeOptions.end]
          : time.rangeOptions.start | time.rangeOptions.end,
    };
  }
};

export const createDataReducer = <T, F>() => (
  state: State<T, F>,
  action: Action<T, F>,
): State<T, F> => {
  switch (action.type) {
    case Actions.FETCHING:
      return {
        ...state,
        isLoading: true,
      };
    case Actions.TOGGLE_FETCHING:
      return {
        ...state,
        isLoading: !state.isLoading,
      };
    case Actions.UPDATE_DATA:
      return {
        ...state,
        data: [...action.payload, ...state.data],
      };
    case Actions.SET_DATA:
      const {
        data,
        currentQueryRef,
        requestQuery,
        pagination: {prevPagination, maintainPages, ...rest},
      } = action.payload;

      if (requestQuery !== currentQueryRef.current) {
        return state;
      }
      const finalData =
        maintainPages && prevPagination.page < state.pagination.page
          ? state.data.concat(data)
          : data;

      return {
        ...state,
        isLoading: false,
        data: finalData,
        pagination: {
          ...state.pagination,
          ...rest,
        },
      };
    case Actions.TOGGLE_SORT:
      let newOrder;
      switch (state.sort?.[action.payload.key]) {
        case SortOrders.ASC:
          newOrder = SortOrders.DESC;
          break;
        case SortOrders.DESC:
          newOrder = undefined;
          break;
        default:
          newOrder = SortOrders.ASC;
          break;
      }
      return {
        ...state,
        sort: {
          ...state.sort,
          [action.payload.key]: newOrder,
        },
      };
    case Actions.SET_SORT_OPTIONS:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          ...action.payload.initialPagination,
        },
        sort: action.payload.sortOption,
      };
    case Actions.SET_AGGREGATION:
      return {
        ...state,
        aggregation: action.payload,
      };

    case Actions.SET_TIMEWINDOW:
      return {
        ...state,
        timeWindow: action.payload,
      };
    case Actions.SET_PAGINATION:
      if (typeof action.payload === 'function') {
        return {
          ...state,
          pagination: {
            ...state.pagination,
            ...action.payload(state.pagination),
          },
        };
      } else {
        return {
          ...state,
          pagination: {...state.pagination, ...action.payload},
        };
      }
    case Actions.SET_QUERY_PARAM: {
      const {newPagination, ...params} = action.payload;
      const pagination = newPagination ? {...state.pagination, ...newPagination} : state.pagination;

      return {
        ...state,
        pagination,
        queryParams: {
          ...state.queryParams,
          ...params,
        },
      };
    }
    case Actions.RESET_QUERY_PARAMS:
      return {
        ...state,
        queryParams: undefined,
      };
    case Actions.RESET_QUERY: {
      const {pagination} = action.payload;
      return {
        ...state,
        ...action.payload,
        pagination: pagination ? {...state.pagination, ...pagination} : state.pagination,
      };
    }
    case Actions.SET_FILTERS: {
      const {newPagination, filters} = action.payload;
      const pagination = newPagination ? {...state.pagination, ...newPagination} : state.pagination;

      return {
        ...state,
        filters,
        pagination,
      };
    }
    case Actions.SET_SEARCH_VALUE:
      return {
        ...state,
        isLoading: true,
        search: action.payload,
      };
    default:
      throw new Error('Action not supported');
  }
};

export const buildQuery = <T, Filters>({
  filters,
  pagination,
  timeWindow,
  aggregation,
  sort,
  // search,
  queryParams,
}: Omit<State<T, Filters>, 'isLoading' | 'data' | 'filters'> & {
  filters?: FilterState<Filters>[];
}) => {
  const timeFilter = timeWindow && getTimeFilter(timeWindow);
  const searchQuery: {
    filters?: (
      | FilterState<Filters>
      | {key: 'time'; operator: FilterOperator; value: number | number[]}
    )[];
    sort?: string;
    // search?: string;
    aggregation?: string;
    interval?: string;
    page?: number;
    limit: number;
  } = {limit: pagination.limit};

  if (filters?.length) {
    searchQuery.filters = filters;
  }

  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        searchQuery[key] = value;
      }
    });
  }

  if (timeFilter) {
    searchQuery.filters = (searchQuery.filters || []).concat([timeFilter]);
  }

  if (aggregation) {
    searchQuery.aggregation = aggregation.func;
    searchQuery.interval = `${aggregation.interval.value} ${aggregation.interval.unit}`;
  }

  const filteredSort = Object.entries(sort || {}).reduce<string[]>((final, [key, value]) => {
    if (!value) {
      return final;
    }
    final.push(`${key} ${value}`);
    return final;
  }, []);

  if (filteredSort.length) {
    searchQuery.sort = filteredSort.join(',');
  }

  searchQuery.page = pagination.page;

  if (filters?.length) {
    searchQuery.filters = filters;
  }

  return queryStringify(searchQuery);
};
